// Cloze markers stay in stored text (`{{c1::answer}}`) so existing cards,
// search and study rendering keep working. The editor never shows them.

const clozeEditors = new WeakSet();

export function clozeRegex() {
  return /\{\{c(\d+)::([\s\S]*?)(?:::([\s\S]*?))?\}\}/g;
}

export function clozeParts(text) {
  return [...String(text ?? '').matchAll(clozeRegex())].map((m) => ({
    n: Number(m[1]),
    answer: m[2] ?? '',
    hint: m[3] ?? '',
  }));
}

export function blankSyntax(n, answer, hint) {
  const num = Number(n) > 0 ? Number(n) : 1;
  const hintPart = hint ? `::${hint}` : '';
  return `{{c${num}::${answer ?? ''}${hintPart}}}`;
}

/** Sentence as a person reads it, with the hidden words left in. */
export function stripCloze(text) {
  return String(text ?? '').replace(clozeRegex(), (_m, _n, answer) => answer ?? '');
}

export function enableBlanks(editor) {
  clozeEditors.add(editor);
}

export function blanksEnabled(editor) {
  return clozeEditors.has(editor);
}


function splitTextNode(node) {
  const text = node.text ?? '';
  if (!text.includes('{{c')) return null;
  if (node.marks?.some((m) => m.type === 'code')) return null;
  const parts = [];
  let last = 0;
  let hit = false;
  for (const m of text.matchAll(clozeRegex())) {
    const n = Number(m[1]);
    if (n < 1) continue;
    hit = true;
    if (m.index > last) {
      const piece = { type: 'text', text: text.slice(last, m.index) };
      if (node.marks?.length) piece.marks = node.marks;
      parts.push(piece);
    }
    const answer = m[2] ?? '';
    parts.push({
      type: 'blank',
      attrs: { n, hint: m[3] ?? '' },
      ...(answer ? { content: [{ type: 'text', text: answer }] } : {}),
    });
    last = m.index + m[0].length;
  }
  if (!hit) return null;
  if (last < text.length) {
    const piece = { type: 'text', text: text.slice(last) };
    if (node.marks?.length) piece.marks = node.marks;
    parts.push(piece);
  }
  return parts;
}

/** Older blanks stored the hidden word on `attrs.answer`. The editor types it as text. */
function migrateBlank(node) {
  const answer = typeof node.attrs?.answer === 'string' ? node.attrs.answer : '';
  const hasContent = Array.isArray(node.content) && node.content.some((child) => child?.text);
  if (!answer || hasContent) return node;
  return {
    type: 'blank',
    attrs: { n: node.attrs?.n ?? 1, hint: node.attrs?.hint ?? '' },
    content: [{ type: 'text', text: answer }],
  };
}

/**
 * Turn `{{c1::answer}}` text into blank nodes so the editor can show chips.
 * Documents that already use chips are returned unchanged.
 */
export function withBlanks(doc) {
  if (!doc || typeof doc !== 'object') return doc;
  let changed = false;
  const visit = (node) => {
    if (!node || typeof node !== 'object') return node;
    if (node.type === 'blank') {
      const next = migrateBlank(node);
      if (next !== node) changed = true;
      return next;
    }
    if (node.type === 'codeBlock') return node;
    if (node.type === 'text') {
      const parts = splitTextNode(node);
      if (!parts) return node;
      changed = true;
      return parts;
    }
    if (!Array.isArray(node.content)) return node;
    let childChanged = false;
    const content = [];
    for (const child of node.content) {
      const next = visit(child);
      if (Array.isArray(next)) {
        content.push(...next);
        childChanged = true;
      } else {
        if (next !== child) childChanged = true;
        content.push(next);
      }
    }
    if (!childChanged) return node;
    changed = true;
    return { ...node, content };
  };
  const next = visit(doc);
  return changed ? next : doc;
}
